<script setup>
import { computed, ref } from 'vue';
import { t } from '../i18n';
import { formatLevel, formatMonth, pathSub, pathTitle } from '../achievements';

// The whole path in a sheet (docs/achievements/SPEC.md, «Проект v2»): moments by year, newest first; what was
// before the start folds into «Первый заход» (after a comeback) or «До отсчёта» at the bottom.
const props = defineProps({
  path: {
    type: Array,
    required: true,
  },
  before: {
    type: Object,
    default: null,
  },
  // 'first' | 'return' | 'custom'
  mode: {
    type: String,
    default: 'first',
  },
});

const beforeOpen = ref(false);

function byYear(events) {
  const groups = [];
  for (const event of events) {
    const year = event.date.slice(0, 4);
    if (groups.at(-1)?.year !== year) {
      groups.push({ year, events: [] });
    }
    groups.at(-1).events.push(event);
  }
  return groups;
}

const years = computed(() => byYear(props.path));
</script>

<template>
  <div>
    <section
      v-for="group in years"
      :key="group.year"
      class="mb-3"
    >
      <h4 class="text-[0.8125rem] font-medium text-slate-500">
        {{ group.year }}
      </h4>
      <ul class="divide-y divide-slate-800">
        <li
          v-for="event in group.events"
          :key="`${event.kind}-${event.date}-${event.value ?? ''}`"
          class="py-2.5"
        >
          <p class="text-[0.9375rem] text-slate-100 sm:text-sm">
            {{ pathTitle(event) }}
          </p>
          <p class="text-[0.8125rem] text-slate-400">
            {{ pathSub(event) }}
          </p>
        </li>
      </ul>
    </section>

    <section
      v-if="before"
      class="border-t border-slate-800 pt-1"
    >
      <button
        type="button"
        class="flex min-h-14 w-full items-center justify-between gap-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400"
        :aria-expanded="beforeOpen"
        @click="beforeOpen = !beforeOpen"
      >
        <span class="min-w-0">
          <span class="block text-[0.9375rem] text-slate-200 sm:text-sm">
            {{ t(mode === 'return' ? 'achievements.firstRun' : 'achievements.beforeStart', { from: formatMonth(before.from), to: formatMonth(before.to) }) }}
          </span>
          <span class="block text-[0.8125rem] text-slate-500">
            {{ t('achievements.beforeBest', { level: formatLevel(before.best_value), count: before.path.length }) }}
          </span>
        </span>
        <span
          class="shrink-0 text-lg leading-none text-slate-500 transition-transform"
          :class="beforeOpen ? 'rotate-90' : ''"
          aria-hidden="true"
        >›</span>
      </button>
      <ul
        v-if="beforeOpen"
        class="divide-y divide-slate-800 pl-3"
      >
        <li
          v-for="event in before.path"
          :key="`${event.kind}-${event.date}-${event.value ?? ''}`"
          class="py-2"
        >
          <p class="text-[0.9375rem] text-slate-300 sm:text-sm">
            {{ pathTitle(event) }}
          </p>
          <p class="text-[0.8125rem] text-slate-500">
            {{ pathSub(event) }}
          </p>
        </li>
      </ul>
    </section>
  </div>
</template>
